import { useState, useCallback } from 'react'
import { useQuery, useMutation } from 'convex/react'
import { api } from '@convex/_generated/api'
import type { Doc } from '@convex/_generated/dataModel'

// Re-export types for convenience
export type Building = Doc<'buildings'>
export type Property = Doc<'properties'>

// Discriminated union for listing items (matches Convex query response)
export type ListingItem =
  | { type: 'building'; data: Building & { specs_range: BuildingSpecsRange } }
  | { type: 'property'; data: Property }

// Building specs range (computed min/max from properties)
interface SpecRange {
  min: number | null
  max: number | null
}

interface BuildingSpecsRange {
  area_m2: SpecRange
  bedrooms: SpecRange
  bathrooms: SpecRange
}

// Item type discriminator
export type ListingItemType = 'building' | 'property'

// Filter parameters for list query
export interface ListingsQueryParams {
  featured?: boolean
}

/**
 * Hook for fetching unified listings (buildings + properties merged).
 * Uses Convex's real-time queries - data updates automatically when it changes.
 *
 * Returns items sorted by sort_order ascending (nulls last), then created_at descending.
 * Only returns published items, excludes building_only properties.
 */
export function useListings(params: ListingsQueryParams = {}) {
  const data = useQuery(api.listings.list, {
    featured: params.featured,
  })

  // Convex queries auto-refresh, but we provide refetch for API compatibility
  const refetch = useCallback(() => {
    // No-op: Convex queries are real-time and auto-update
  }, [])

  return {
    data,
    isLoading: data === undefined,
    error: null as Error | null, // Convex throws on error
    refetch,
  }
}

// Reorder item type (includes type discriminator)
export interface ListingsReorderItem {
  type: ListingItemType
  id: string
  sort_order: number
}

// Reorder response type
export interface ListingsReorderResponse {
  success: boolean
  updated_count: number
}

/**
 * Hook for reordering unified listings (buildings + properties together).
 * Returns a mutation function that can be called with an array of items
 * with their types and new sort_order values.
 *
 * This updates sort_order in both the buildings and properties tables.
 */
export function useReorderListings() {
  const reorderMutation = useMutation(api.listings.reorder)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const mutateAsync = useCallback(async (items: ListingsReorderItem[]): Promise<ListingsReorderResponse> => {
    setIsPending(true)
    setError(null)
    try {
      const result = await reorderMutation({
        items: items.map(item => ({
          type: item.type,
          id: item.id,
          sort_order: item.sort_order,
        })),
        ip_address: undefined,
        user_agent: navigator.userAgent,
      })
      return result as ListingsReorderResponse
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)))
      throw err
    } finally {
      setIsPending(false)
    }
  }, [reorderMutation])

  return {
    mutate: mutateAsync,
    mutateAsync,
    isPending,
    error,
  }
}
